/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { ElementRef } from '@angular/core';
import * as Hammer from 'hammerjs';

export enum AbortOrContinue {
	Abort,
	Continue,
}

/**
 * A utility class for handling drag-and-drop from a given DOM Element,
 * which is specified in the constructor, along with three callback functions:
 * dragStart, drag and dragEnd.  The dragStart and drag methods each return a
 * Abort or Continue value; if Abort, this handler stops calling the callback
 * functions for the rest of the drag-n-drop gesture.
 *
 * IDEA for FUTURE IMPROVEMENT (thanks to Kurtis):
 * fwiw, my idea of an... "ideal drag handler" in terms of code prettiness looks something like:
 *
 *     <my-component draggable dragStart="dragStart(data)" drag="drag(data)" dragEnd="dragEnd(data)"></my-component>
 *
 * where draggable is an attribute directive which has those 3 output functions calling my-components representations.
 * So that sort of connection is all handled internally by angular. Then said attribute directive uses the above class
 * to drive it.  I suspect that might be too slow though.
 *
 * gives the implementing component the ability to omit all of the following
 *
 *   @ViewChild('clipboardDragAndDropElement', { static: false }) clipboardDragAndDropElement: ElementRef;
 *
 *       :
 *   ngAfterViewInit() {
 *     const dragStart = event => this.dragStart(event);
 *     const drag = event => this.drag(event);
 *     const dragEnd = () => this.dragEnd(event);
 *     this.dragAndDropHandler = new DragAndDropHandler(this.clipboardDragAndDropElement, dragStart, drag, dragEnd);
 *   }
 */

//the one declared in the hammer typings is an implict any <_<
type HammerCallback = (event: Event) => void;

export class DragAndDropHandler {
	private _dragStartHandler: (event: MouseEvent) => AbortOrContinue;
	private _dragHandler: (event: MouseEvent) => AbortOrContinue;
	private _dragEndHandler: (event: MouseEvent) => void;
	private _mousePressHandler: (event: MouseEvent) => AbortOrContinue;

	private isDragging: boolean;
	private firstMouseDown: MouseEvent;
	private readonly DRAG_START_THRESHOLD = 5;

	private hammerEvents: Array<{ element: EventTarget; event: string; cb: HammerCallback }>;

	constructor(
		dragAndDropElement: ElementRef | Element,
		dragStart: (event: MouseEvent) => AbortOrContinue,
		drag: (event: MouseEvent) => AbortOrContinue,
		dragEnd: (event: MouseEvent) => void,
		mousePress?: (event: MouseEvent) => AbortOrContinue
	) {
		const element =
			dragAndDropElement instanceof ElementRef ? dragAndDropElement.nativeElement : dragAndDropElement;
		this._dragStartHandler = dragStart;
		this._dragHandler = drag;
		this._dragEndHandler = dragEnd;
		this._mousePressHandler = mousePress;

		this.hammerEvents = [
			{ element, event: 'mousedown', cb: (e) => this._internalDragStart(e) },
			{ element: window, event: 'mousemove', cb: (e) => this._internalDrag(e as MouseEvent) },
			{ element: window, event: 'mouseup', cb: (e) => this._internalDragEnd(e as MouseEvent) },
			{ element, event: 'touchstart', cb: (e) => this._internalDragStart(this.massageTouchEvent(e)) },
			{ element: window, event: 'touchmove', cb: (e) => this._internalDrag(this.massageTouchEvent(e)) },
			{ element: window, event: 'touchend', cb: (e) => this._internalDragEnd(this.massageTouchEvent(e)) },
		];

		// Subscribe to hammer events
		this.hammerEvents.forEach((ev) => {
			Hammer.on(ev.element, ev.event, ev.cb);
		});
	}

	public destroy() {
		// Unsubscribe to hammer events
		this.hammerEvents.forEach((ev) => {
			Hammer.off(ev.element, ev.event, ev.cb);
		});
	}

	//HammerInput doesn't actually declare .button
	private _internalDragStart(event: any): void {
		// only need to handle left mouse down to not interfere with context menu gesture
		if (event.button === 0) {
			this.firstMouseDown = event;
			if (this._mousePressHandler) {
				const status: AbortOrContinue = this._mousePressHandler(event);
				if (status === AbortOrContinue.Abort) {
					this.firstMouseDown = undefined;
				}
			}
			this.isDragging = false;
		}
	}

	private _internalDrag(event: MouseEvent): void {
		if (!this.isDragging) {
			// the real start of dragging happens when user presses the mouse and starts to move horizontally and/orvertically
			// this is for not treating click as a paste request. See TP-620
			if (this.firstMouseDown) {
				if (
					Math.abs(event.screenX - this.firstMouseDown.screenX) >= this.DRAG_START_THRESHOLD ||
					Math.abs(event.screenY - this.firstMouseDown.screenY) >= this.DRAG_START_THRESHOLD
				) {
					this.isDragging = true;
					const choice = this._dragStartHandler(event);
					if (choice === AbortOrContinue.Abort) {
						this.isDragging = false;
					}
				}
			}
			return;
		}
		const status = this._dragHandler(event);
		if (status === AbortOrContinue.Abort) {
			this.isDragging = false;
		}
	}

	private _internalDragEnd(event: MouseEvent): void {
		this.firstMouseDown = undefined;

		if (!this.isDragging) {
			return;
		}
		this.isDragging = false;

		this._dragEndHandler(event);
	}

	/**
	 * Returns a fake mouse event based on a touch event.
	 */
	private massageTouchEvent(touchEvent: any): any {
		let event = null;
		if (touchEvent.targetTouches.length > 0) {
			event = touchEvent.targetTouches[0];
			event.x = event.screenX;
			event.y = event.screenY;
			event.button = 0;
		} else {
			event = touchEvent;
		}
		return event;
	}
}
